import { timezoneDayjs as dayjs, URL_KEYS } from '@travel/traveler-core/utils/jrdpHelper';
import {
  isEmptyObject,
  isNotEmptyArray,
  isNotEmptyValue,
  parseQueryString,
  stringify,
} from '@travel/utils';

import {
  COMPONENT_PRICE_FILTER,
  MAX_PRICE_FILTER_RANGE,
  MIN_PRICE_FILTER_RANGE,
} from 'constants/common';
import paths from 'constants/paths';
import { PAGE_INDICATOR } from 'constants/seo';
import {
  HOTEL_LIST_DEFAULT_SORT,
  HOTEL_LIST_SORT,
  PLAN_DEFAULT_SORT,
  PLAN_DETAIL_SORT,
  PLAN_LIST_SORT,
} from 'constants/sort';

import type { SortKeyMapping } from 'constants/sort';
import type { Area, Breadcrumb, DisabledComponent, URLSearchQuery } from 'Common-Types';
import type { HotelItem, SortCategory as HotelListSort } from 'HotelList-Types';
import type { SortCategory as PlanDetailSort } from 'PlanDetails-Types';
import type { SortCategory as PlanListSort } from 'PlanList-Types';

export const DEFAULT_ROOM = 1;
export const DEFAULT_ADULT = 2;
export const DEFAULT_CHILDREN = 0;
export const DEFAULT_INFANT_BED_MEAL = 0;
export const DEFAULT_INFANT_NONE = 0;
export const DEFAULT_STAY_NIGHT = 1;
export const DEFAULT_TRAVEL_DAYS = 2;

export const MAX_ADULT_COUNT = 6;
export const MAX_ROOM_COUNT = 9;
export const MAX_STAY_NIGHT = 5;
export const imageThumbnailSize = { pc: '10vw' };

const EARLIEST_BOOKING_DATE_TIME_CUTOFF_HOUR = 16;
const DAYS_FROM_TODAY_FOR_EARLIEST_BOOKING = 1;

export const enum BreadcrumbType {
  HOTEL_LIST = 'hotel_list',
  PLAN_LIST = 'hotel_info_plan',
  HOTEL_LIST_MAP = 'hotel_list_map',
}

export const getEarliestDate = (formatType?: string) => {
  const today = dayjs();
  const isTimeOnOrAfterCutoffTime = today.hour() >= EARLIEST_BOOKING_DATE_TIME_CUTOFF_HOUR;
  const earliestPossibleBookingDay = today.add(
    DAYS_FROM_TODAY_FOR_EARLIEST_BOOKING + (isTimeOnOrAfterCutoffTime ? 1 : 0),
    'day',
  );
  return !!formatType ? earliestPossibleBookingDay.format(formatType) : earliestPossibleBookingDay;
};
export const getEndOfSalesPeriodDate = (
  jrdpServiceSalesEndDateString?: string,
  formatType?: string,
) => {
  const jrdpServiceSalesEndDate = dayjs(jrdpServiceSalesEndDateString);
  const today = dayjs();
  let salesPeriodAfterSixMonths = today.add(6, 'month');
  //if our final date defaults to the end of the month because of not enough days, go to the 1st of next month
  if (salesPeriodAfterSixMonths.date() !== today.date()) {
    salesPeriodAfterSixMonths = salesPeriodAfterSixMonths.add(1, 'day');
  }

  let endSalesPeriodDate;
  if (!jrdpServiceSalesEndDateString || !jrdpServiceSalesEndDate.isValid()) {
    endSalesPeriodDate = salesPeriodAfterSixMonths;
  } else {
    endSalesPeriodDate = jrdpServiceSalesEndDate.isBefore(salesPeriodAfterSixMonths, 'day')
      ? jrdpServiceSalesEndDate
      : salesPeriodAfterSixMonths;
  }

  return !!formatType ? endSalesPeriodDate.format(formatType) : endSalesPeriodDate;
};

export function getDefaultURLCustomListRequest<S extends string>(query?: URLSearchQuery) {
  const isValidPriceRange = isValidPriceArrangement(
    query?.[URL_KEYS.MIN_PRICE],
    query?.[URL_KEYS.MAX_PRICE],
  );

  const validSort = query?.[URL_KEYS?.SORT] ? query?.[URL_KEYS?.SORT] : undefined;
  const features = Array.isArray(query?.[URL_KEYS.FEATURES])
    ? query?.[URL_KEYS.FEATURES]
    : String(query?.[URL_KEYS.FEATURES] ?? '').split(',');

  const isNumbersOnlyRegex = /^\d+$/;
  const featureManagementNames = features.filter(
    (feature) => feature && !feature.match(isNumbersOnlyRegex),
  );

  return {
    features: features.filter((feature) => feature && feature.match(isNumbersOnlyRegex)),
    sort: validSort as S,
    minPricePerAdult: isValidPriceRange ? Number(query?.[URL_KEYS.MIN_PRICE]) : undefined,
    maxPricePerAdult: isValidPriceRange ? Number(query?.[URL_KEYS.MAX_PRICE]) : undefined,
    ...(isNotEmptyArray(featureManagementNames) && { featureManagementNames }),
  };
}

export function toSearchQueryString(queryObject: {
  [key: string]: undefined | string | number | Array<string | number>;
}): string {
  if (!queryObject || !isNotEmptyArray(Object.keys(queryObject))) return '';

  return Object.entries(queryObject)
    .reduce((result: Array<string>, [key, value]) => {
      let formattedValue = value;

      if (!isNotEmptyValue(value as string) || (Array.isArray(value) && !isNotEmptyArray(value)))
        return result;

      if (Array.isArray(value)) formattedValue = value.join(',');
      return [...result, `${key}=${formattedValue}`];
    }, [])
    .join('&');
}

export const getValidAccommodationsPath = (accommodations?: Array<Area>) =>
  accommodations
    ?.reduce((result: Array<string>, accommodation) => {
      if (!accommodation?.pathId) return result;
      return [...result, accommodation.pathId];
    }, [])
    .join(',') || '';

export const isValidPriceArrangement = (
  minPrice?: string | number,
  maxPrice?: string | number,
): boolean => {
  const isValidSelectedRange = minPrice !== undefined && maxPrice !== undefined;
  if (!isValidSelectedRange) return false;
  else if (
    Number(minPrice) === MIN_PRICE_FILTER_RANGE &&
    Number(maxPrice) === MAX_PRICE_FILTER_RANGE
  )
    return false;
  else return Number(minPrice) <= Number(maxPrice);
};

function _getValidSortValue<T>(
  sortObject: SortKeyMapping<T>,
  defaultValue: string,
  value?: string,
) {
  const lowerCaseValued = value?.toLowerCase();
  return lowerCaseValued && sortObject[lowerCaseValued]
    ? sortObject[lowerCaseValued].value
    : sortObject[defaultValue].value;
}

export const getHotelListValidSortValue = (value?: string) => {
  return _getValidSortValue<HotelListSort>(HOTEL_LIST_SORT, HOTEL_LIST_DEFAULT_SORT, value);
};

export const getPlanListValidSortValue = (value?: string) => {
  return _getValidSortValue<PlanListSort>(PLAN_LIST_SORT, PLAN_DEFAULT_SORT, value);
};

export const getPlanDetailValidSortValue = (value?: string) => {
  return _getValidSortValue<PlanDetailSort>(PLAN_DETAIL_SORT, PLAN_DEFAULT_SORT, value);
};

export const getDataLayersArrayParams = (providers: HotelItem[]) =>
  providers.reduce((result, proObj) => {
    return {
      providerIds: (result['providerIds'] ? `${result['providerIds']},` : '') + proObj.id,
      departureStation:
        (result['departureStation'] && proObj?.departureStation?.code
          ? `${result['departureStation']},`
          : '') + proObj?.departureStation?.code,
      destinationStation:
        (result['destinationStation'] && proObj?.destinationStation?.code
          ? `${result['destinationStation']},`
          : '') + proObj?.destinationStation?.code,
    };
  }, {});

export const normalizePathIdentifier = (path: string) => {
  const str = path.endsWith('/') ? path.replace(/\/+$/, '') : path;
  const pathDetails = {
    normalizePath: str,
    pageNumber: 1,
  };

  if (str.includes(PAGE_INDICATOR)) {
    const newPath = str.split(PAGE_INDICATOR);
    const page = Number(newPath[1]);
    pathDetails.normalizePath = newPath[0];
    pathDetails.pageNumber = isNaN(page) ? 1 : Number(page);
  }
  return pathDetails;
};

export const ensureTrailingSlash = (url: string) => {
  return !url?.endsWith('/') ? `${url}/` : url;
};

export const getPlaceLayers = (breadcrumbs: Array<Breadcrumb>, query, type?: string) => {
  if (isNotEmptyArray(breadcrumbs)) {
    const composedSearchQuery = !isEmptyObject(query)
      ? `?${stringify({
          ...parseQueryString(query),
          [URL_KEYS.PLACE_OF_STAY]: '',
          [URL_KEYS.ACCOMMODATION_AREA]: '',
        })}`
      : '';

    return breadcrumbs?.map((breadcrumb) => {
      if (type === BreadcrumbType.HOTEL_LIST_MAP) {
        return {
          name: breadcrumb.name,
          link: `${paths.providerListMap.pathResolver(
            breadcrumb.pathId || '',
          )}${composedSearchQuery}`,
        };
      }
      return {
        name: breadcrumb.name,
        link:
          breadcrumb.type === 'hotel_list'
            ? `${paths.providerList.pathResolver(breadcrumb.pathId || '')}${composedSearchQuery}`
            : `${paths.plansList.pathResolver(breadcrumb.pathId || '')}${composedSearchQuery}`,
      };
    });
  }
  return [];
};

export const isDatedUrl = (params: string) => {
  const urlParams = new URLSearchParams(params);
  const datedKeys = [
    URL_KEYS.ELEMENTARY_CHILDREN,
    URL_KEYS.INFANT_BED_MEALS,
    URL_KEYS.INFANT,
    URL_KEYS.NO_OF_UNITS,
    URL_KEYS.DEPARTURE_DATE,
    URL_KEYS.RETURN_DATE,
    URL_KEYS.CHECK_IN_DATE,
    URL_KEYS.CHECK_OUT_DATE,
    URL_KEYS.DEPARTURE,
    URL_KEYS.PLACE_OF_STAY,
    URL_KEYS.ADULTS,
    URL_KEYS.ACCOMMODATION_AREA,
  ];

  let isDatedUrl = false;
  const keys = Array.from(urlParams.keys());
  isDatedUrl = keys.some((key) => datedKeys.includes(key as URL_KEYS));
  return isDatedUrl;
};

export const getDisableControl = (controlSettings: DisabledComponent) => {
  let shouldBeDisplayed = false;
  if (!isEmptyObject(controlSettings)) {
    const { start_time, end_time, component_name } = controlSettings;
    const today = dayjs();
    if (component_name === COMPONENT_PRICE_FILTER && start_time && end_time) {
      shouldBeDisplayed =
        today.isSameOrAfter(dayjs(start_time)) && today.isSameOrBefore(dayjs(end_time));
    }
  }
  return shouldBeDisplayed;
};
